import React, { useState, useEffect, useRef } from 'react';
import { Calendar, Clock, Users, ChevronRight, X } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';

const Reservations = () => {
  const { t, language } = useLanguage();
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [captchaChecked, setCaptchaChecked] = useState(false);
  const [showCaptchaError, setShowCaptchaError] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    date: '',
    time: '',
    guests: '',
    message: ''
  });

  const [dialogPosition, setDialogPosition] = useState(0);
  const [savedScrollY, setSavedScrollY] = useState(0);
  const dialogRef = useRef(null);

  // GALERİ SİSTEMİ: Modal açma - ekran neredeyse modal orada açılsın
  const handleEventSelect = (eventType) => {
    const scrollY = window.scrollY;
    const viewportHeight = window.innerHeight;
    setSavedScrollY(scrollY);
    setDialogPosition(scrollY + viewportHeight / 2);
    setSelectedEvent(eventType);
    setIsDialogOpen(true);
  };

  // ÇALIŞAN SCROLL KİLİDİ - Dikey ekranda çalışıyor
  useEffect(() => {
    if (!isDialogOpen) return;
    
    const scrollY = savedScrollY;
    
    // Body'yi fixed yap ve scroll pozisyonunu koru
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    
    // Touch eventlerini engelle - modal içi hariç
    const preventTouch = (e) => {
      // Modal içindeki scrollable alanda ise engelleme
      if (e.target.closest('.dialog-scrollable')) {
        return;
      }
      e.preventDefault();
    };
    
    // Wheel eventini engelle - modal içi hariç
    const preventWheel = (e) => {
      if (e.target.closest('.dialog-scrollable')) {
        return;
      }
      e.preventDefault();
    };
    
    document.addEventListener('touchmove', preventTouch, { passive: false });
    document.addEventListener('wheel', preventWheel, { passive: false });
    
    return () => {
      // Önce scroll-behavior'ı kapat
      document.documentElement.style.scrollBehavior = 'auto';
      
      // Stilleri temizle
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
      document.removeEventListener('touchmove', preventTouch);
      document.removeEventListener('wheel', preventWheel);
      
      // Anında scroll et
      window.scrollTo(0, scrollY);
      
      // scroll-behavior'ı geri aç
      document.documentElement.style.scrollBehavior = '';
    };
  }, [isDialogOpen, savedScrollY]);

  // DİKEY EKRAN: Klavye açıldığında modal'ı yukarı al
  useEffect(() => {
    if (!isDialogOpen) return;
    if (!window.visualViewport) return;
    
    const isLandscape = window.innerWidth > window.innerHeight;
    const isTablet = 'ontouchstart' in window && (window.innerWidth >= 768 || window.innerHeight >= 768);
    
    // Sadece dikey tablet için
    if (!isTablet || isLandscape) return;
    
    const initialHeight = window.innerHeight;
    
    const handleKeyboard = () => {
      const currentHeight = window.visualViewport.height;
      const heightDiff = initialHeight - currentHeight;
      
      if (heightDiff > 150) {
        // Klavye açık - modal'ı yukarı al
        const newPosition = savedScrollY + (currentHeight / 2) - 50;
        setDialogPosition(newPosition);
      } else {
        // Klavye kapalı - orijinal pozisyona dön
        setDialogPosition(savedScrollY + (initialHeight / 2));
      }
    };
    
    window.visualViewport.addEventListener('resize', handleKeyboard);
    
    return () => {
      window.visualViewport.removeEventListener('resize', handleKeyboard);
    };
  }, [isDialogOpen, savedScrollY]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Geçmiş tarih kontrolü
    const selectedDate = new Date(formData.date + 'T' + formData.time);
    const now = new Date();
    if (selectedDate < now) {
      toast.error(language === 'de' 
        ? 'Ungültiges Datum' 
        : 'Invalid Date', {
        description: language === 'de' 
          ? 'Bitte wählen Sie ein zukünftiges Datum und eine zukünftige Uhrzeit.' 
          : 'Please select a future date and time.'
      });
      return;
    }
    
    // CAPTCHA kontrolü
    if (!captchaChecked) {
      setShowCaptchaError(true);
      return;
    }
    setShowCaptchaError(false);
    
    setIsSubmitting(true);
    
    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          access_key: '18516033-0ab7-4642-8d78-45d2bf7a6e54',
          subject: `Neue Reservierung: ${selectedEvent?.name}`,
          from_name: 'SLÁI Events Website',
          to: 'info@slaievents.com',
          event_type: selectedEvent?.name,
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          preferred_date: formData.date,
          preferred_time: formData.time,
          number_of_guests: formData.guests,
          special_requests: formData.message || 'Keine',
        }),
      });

      const result = await response.json();
      
      if (result.success) {
        toast.success(t.reservations.successTitle, {
          description: t.reservations.successDescription,
        });
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          date: '',
          time: '',
          guests: '',
          message: ''
        });
        setCaptchaChecked(false);
        setShowCaptchaError(false);
        setIsDialogOpen(false);
        setSelectedEvent(null);
        document.body.style.overflow = '';
      } else {
        throw new Error('Form submission failed');
      }
    } catch (error) {
      toast.error(
        language === 'de' ? 'Fehler' : 'Error',
        {
          description: language === 'de' ? 'Es gab ein Problem beim Senden. Bitte versuchen Sie es erneut.' : 'There was a problem sending. Please try again.',
        }
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Event images - local optimized WebP files
  const eventImages = [
    './images/event-foodtruck.webp',
    './images/event-gartenparty.webp',
    './images/event-cocktail.jpg',
    './images/event-geburtstag.webp',
    './images/event-firmen.webp',
    './images/event-dream.webp'
  ];

  return (
    <div className="min-h-screen pt-16 bg-[#f8f6f3]">
      {/* Hero Section */}
      <section className="py-16 sm:py-24 bg-[#1a1a1a]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <p className="text-xs tracking-[0.3em] uppercase text-[#8b7355] mb-4" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              {language === 'de' ? 'BUCHEN SIE IHR EVENT' : 'BOOK YOUR EVENT'}
            </p>
            <h1 className="text-4xl sm:text-6xl md:text-7xl text-white mb-4" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 400 }}>
              {t.reservations.title}
            </h1>
            <div className="w-16 h-[1px] bg-white/40 mx-auto mb-6" />
            <p className="text-base sm:text-lg text-white/70 max-w-2xl mx-auto" style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}>
              {t.reservations.subtitle}
            </p>
          </div>

          {/* Event Type Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 max-w-7xl mx-auto">
            {t.reservations.eventTypes.map((eventType, index) => {
              const isSpecialCard = eventType.isSpecial;
              
              return (
              <Card
                key={index}
                className={`rounded-xl border ${isSpecialCard ? 'border-[#8b7355]' : 'border-white/20'} bg-white/5 shadow-none hover:bg-white/10 transition-all duration-500 cursor-pointer group overflow-hidden`}
                onClick={() => handleEventSelect(eventType)}
              >
                {/* Event Image */}
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={eventImages[index]}
                    alt={eventType.name}
                    className={`w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700 ${isSpecialCard ? 'object-top' : ''}`}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1a1a1a] via-transparent to-transparent" />
                </div>
                
                <CardHeader className="text-center pb-4 border-b border-white/10 -mt-12 relative z-10 bg-[#1a1a1a] pt-6">
                  <CardTitle className="text-2xl sm:text-3xl text-white mb-2" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>{eventType.name}</CardTitle>
                  <CardDescription className="text-sm text-white/60" style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}>{eventType.description}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4 pt-6 bg-[#1a1a1a]">
                  <div className="flex items-center gap-3 text-white/60">
                    <Users className="w-5 h-5 text-[#8b7355]" />
                    <span className="text-sm" style={{ fontFamily: "'Montserrat', sans-serif" }}>{t.reservations.minGuests} {eventType.minGuests} {t.reservations.guests}</span>
                  </div>
                  <div className="flex items-center gap-3 text-white/60">
                    <Clock className="w-5 h-5 text-[#8b7355]" />
                    <span className="text-sm" style={{ fontFamily: "'Montserrat', sans-serif" }}>{eventType.duration}</span>
                  </div>
                  <Button
                    className="w-full mt-4 bg-[#3a3a3a] text-white border border-[#e67e22] hover:bg-[#4a4a4a] text-xs tracking-[0.15em] uppercase rounded-none group-hover:shadow-lg transition-all duration-300"
                    style={{ fontFamily: "'Montserrat', sans-serif" }}
                  >
                    {t.reservations.selectEvent}
                    <ChevronRight className="w-4 h-4 ml-2 transform group-hover:translate-x-1 transition-transform" />
                  </Button>
                </CardContent>
              </Card>
            )})}
          </div>
        </div>
      </section>

      {/* Information Section */}
      <section className="py-16 sm:py-24 bg-[#1a1a1a]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <p className="text-xs tracking-[0.3em] uppercase text-[#8b7355] mb-4 text-center" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              {language === 'de' ? 'SO FUNKTIONIERT ES' : 'HOW IT WORKS'}
            </p>
            <h2 className="text-4xl sm:text-5xl text-white mb-6 text-center" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 400 }}>
              {t.reservations.howItWorksTitle}
            </h2>
            <div className="w-16 h-[1px] bg-white/40 mx-auto mb-16" />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
              {t.reservations.steps.map((step, index) => (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 border border-white/40 flex items-center justify-center mx-auto mb-6 text-white text-2xl sm:text-3xl" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                    {index + 1}
                  </div>
                  <h3 className="text-xl sm:text-2xl text-white mb-3" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>{step.title}</h3>
                  <p className="text-white/60 text-sm" style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}>{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* GALERİ SİSTEMİ: Modal - absolute, ekran neredeyse orada açılır */}
      {isDialogOpen && (
        <>
          {/* Overlay */}
          <div 
            className="fixed inset-0 bg-black/50 z-[9999]"
            onClick={() => setIsDialogOpen(false)}
            onTouchMove={(e) => {
              // Sadece tablet ve masaüstünde engelle (768px üstü)
              if (window.innerWidth >= 768) {
                e.preventDefault();
              }
            }}
          />
          {/* Modal - absolute pozisyon */}
          <div 
            ref={dialogRef}
            className="absolute left-1/2 z-[10000] w-full max-w-[600px] px-4"
            style={{ 
              transform: 'translate(-50%, -50%)',
              top: `${dialogPosition}px`
            }}
            onTouchMove={(e) => {
              // Sadece tablet ve masaüstünde engelle (768px üstü)
              if (window.innerWidth >= 768) {
                e.preventDefault();
              }
            }}
          >
            <div 
              className="dialog-scrollable bg-[#1a1a1a] border-2 border-white/20 rounded-lg p-5 shadow-xl max-h-[80vh] sm:max-h-none overflow-y-auto sm:overflow-visible"
              onTouchMove={(e) => {
                // Sadece tablet ve masaüstünde engelle (768px üstü)
                if (window.innerWidth >= 768) {
                  e.preventDefault();
                }
              }}
            >
              {/* Header */}
              <div className="flex justify-between items-start mb-3">
                <div>
                  <h2 className="text-xl font-semibold tracking-tight text-white">
                    {t.reservations.formTitle} {selectedEvent?.name}
                  </h2>
                  <p className="text-white/60 text-xs mt-1">
                    {t.reservations.formDescription}
                  </p>
                </div>
                <button 
                  onClick={() => setIsDialogOpen(false)}
                  className="text-white/60 hover:text-white p-1"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              {/* Form - kompakt */}
              <form onSubmit={handleSubmit} className="space-y-3">
                {/* Ad ve Email - her zaman yan yana */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label htmlFor="name" className="text-white/70 text-sm">{t.reservations.formLabels.fullName} *</Label>
                    <Input
                      id="name"
                      name="name"
                      placeholder={t.reservations.formPlaceholders.fullName}
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="bg-white/5 border border-white/20 text-white placeholder:text-gray-500 h-10 w-full rounded-md px-3"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="email" className="text-white/70 text-sm">{t.reservations.formLabels.email} *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder={t.reservations.formPlaceholders.email}
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="bg-white/5 border border-white/20 text-white placeholder:text-gray-500 h-10 w-full rounded-md px-3"
                    />
                  </div>
                </div>
                {/* Telefon ve Tarih (sol sütun) - Misafir ve Saat (sağ sütun) */}
                <div className="grid grid-cols-2 gap-3">
                  {/* Sol Sütun: Telefon */}
                  <div className="space-y-1">
                    <Label htmlFor="phone" className="text-white/70 text-sm">{t.reservations.formLabels.phone} *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      placeholder={t.reservations.formPlaceholders.phone}
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      className="bg-white/5 border border-white/20 text-white placeholder:text-gray-500 h-10 w-full rounded-md px-3"
                    />
                  </div>
                  {/* Sağ Sütun: Misafir */}
                  <div className="space-y-1">
                    <Label htmlFor="guests" className="text-white/70 text-sm">{t.reservations.formLabels.numberOfGuests} *</Label>
                    <Input
                      id="guests"
                      name="guests"
                      type="number"
                      placeholder={`Min. ${selectedEvent?.minGuests || 10}`}
                      min={selectedEvent?.minGuests || 10}
                      value={formData.guests}
                      onChange={handleInputChange}
                      required
                      className="bg-white/5 border border-white/20 text-white placeholder:text-gray-500 h-10 w-full rounded-md px-3"
                    />
                  </div>
                </div>
                {/* Tarih ve Saat - aynı grid yapısı */}
                <div className="grid grid-cols-2 gap-3">
                  {/* Sol Sütun: Tarih (Telefon ile aynı hizda) */}
                  <div className="space-y-1">
                    <Label htmlFor="date" className="text-white/70 text-sm">{t.reservations.formLabels.preferredDate} *</Label>
                    <Input
                      id="date"
                      name="date"
                      type="date"
                      value={formData.date}
                      onChange={handleInputChange}
                      min={new Date().toISOString().split('T')[0]}
                      required
                      className="bg-white/5 border border-white/20 text-white h-10 w-full rounded-md px-3"
                    />
                  </div>
                  {/* Sağ Sütun: Saat (Misafir ile aynı hizda) */}
                  <div className="space-y-1">
                    <Label htmlFor="time" className="text-white/70 text-sm">{t.reservations.formLabels.preferredTime} *</Label>
                    <Input
                      id="time"
                      name="time"
                      type="time"
                      value={formData.time}
                      onChange={handleInputChange}
                      required
                      className="bg-white/5 border border-white/20 text-white h-10 w-full rounded-md px-3"
                    />
                  </div>
                </div>
                {/* Özel İstekler */}
                <div className="space-y-1">
                  <Label htmlFor="message" className="text-white/70 text-sm">{t.reservations.formLabels.specialRequests}</Label>
                  <Textarea
                    id="message"
                    name="message"
                    placeholder={t.reservations.formPlaceholders.specialRequests}
                    value={formData.message}
                    onChange={handleInputChange}
                    rows={2}
                    className="bg-white/5 border-white/20 text-white placeholder:text-gray-500"
                  />
                </div>
                <div className="py-1">
                  <div className="flex items-center space-x-3">
                    <Checkbox 
                      id="captcha-reservation" 
                      checked={captchaChecked}
                      onCheckedChange={(checked) => {
                        setCaptchaChecked(checked);
                        if (checked) setShowCaptchaError(false);
                      }}
                      className="border-gray-600 data-[state=checked]:bg-[#3a3a3a] data-[state=checked]:border-[#8b7355]"
                    />
                    <Label htmlFor="captcha-reservation" className="text-white/70 cursor-pointer text-sm">
                      {language === 'de' ? 'Ich bin kein Roboter' : 'I am not a robot'}
                    </Label>
                  </div>
                  {showCaptchaError && (
                    <p className="text-red-500 text-xs mt-1">
                      {language === 'de' ? '⚠️ Bitte bestätigen Sie, dass Sie kein Roboter sind.' : '⚠️ Please confirm that you are not a robot.'}
                    </p>
                  )}
                </div>
                <div className="flex gap-3 pt-1">
                  <Button
                    type="button"
                    className="flex-1 bg-[#3a3a3a] text-white border border-white/30 hover:bg-[#4a4a4a] rounded-none uppercase tracking-[0.1em] text-xs h-10"
                    style={{ fontFamily: "'Montserrat', sans-serif" }}
                    onClick={() => setIsDialogOpen(false)}
                    disabled={isSubmitting}
                  >
                    {t.reservations.formButtons.cancel}
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 bg-[#3a3a3a] text-white border border-[#e67e22] hover:bg-[#4a4a4a] rounded-none uppercase tracking-[0.1em] text-xs h-10"
                    style={{ fontFamily: "'Montserrat', sans-serif" }}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Wird gesendet...' : t.reservations.formButtons.submit}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Reservations;
